import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PlusIcon,
    PhotoIcon,
    TrashIcon,
    PencilSquareIcon,
    XMarkIcon,
    ArrowUpTrayIcon,
    DevicePhoneMobileIcon,
    ComputerDesktopIcon
} from '@heroicons/react/24/outline';
import { API } from '../../config';

export const AdminHeroImages = () => {
    const [heroImages, setHeroImages] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingImage, setEditingImage] = useState(null);
    const [desktopFile, setDesktopFile] = useState(null);
    const [mobileFile, setMobileFile] = useState(null);
    const [loading, setLoading] = useState(false);

    // Preview states
    const [desktopPreview, setDesktopPreview] = useState(null);
    const [mobilePreview, setMobilePreview] = useState(null);

    useEffect(() => {
        fetchHeroImages();
    }, []);

    // Cleanup previews
    useEffect(() => {
        return () => {
            if (desktopPreview) URL.revokeObjectURL(desktopPreview);
            if (mobilePreview) URL.revokeObjectURL(mobilePreview);
        };
    }, [desktopPreview, mobilePreview]);

    const fetchHeroImages = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/hero-images`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHeroImages(response.data);
        } catch (error) {
            toast.error('Failed to fetch hero images');
            console.error('Hero images fetch error:', error);
        }
    };

    const handleImageUpload = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            const previewUrl = URL.createObjectURL(file);
            if (type === 'desktop') {
                setDesktopFile(file);
                setDesktopPreview(previewUrl);
            }
            if (type === 'mobile') {
                setMobileFile(file);
                setMobilePreview(previewUrl);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            if (desktopFile) formData.append('desktop', desktopFile);
            if (mobileFile) formData.append('mobile', mobileFile);

            if (editingImage) {
                await axios.put(`${API}/hero-images/${editingImage.id}`, formData, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Hero image updated!');
            } else {
                await axios.post(`${API}/hero-images`, formData, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Hero image added!');
            }

            resetForm();
            fetchHeroImages();
        } catch (error) {
            toast.error('Failed to save hero image');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setShowAddForm(false);
        setEditingImage(null);
        setDesktopFile(null);
        setMobileFile(null);
        setDesktopPreview(null);
        setMobilePreview(null);
    };

    const handleEdit = (image) => {
        setEditingImage(image);
        setShowAddForm(true);
        setDesktopFile(null);
        setMobileFile(null);
        setDesktopPreview(image.desktop_url);
        setMobilePreview(image.mobile_url);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this hero image?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/hero-images/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Hero image deleted!');
            fetchHeroImages();
        } catch (error) {
            toast.error('Failed to delete hero image');
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm p-8"
        >
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Hero Images</h2>
                    <p className="text-gray-500 text-sm mt-1">Manage homepage banners and sliders</p>
                </div>
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => { resetForm(); setShowAddForm(true); }}
                    className="bg-gray-900 text-white px-5 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm hover:shadow-md flex items-center font-medium text-sm"
                >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Hero Image
                </motion.button>
            </div>

            {/* Add/Edit Hero Image Form */}
            <AnimatePresence>
                {showAddForm && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden mb-8"
                    >
                        <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                                    {editingImage ? <PencilSquareIcon className="h-5 w-5 mr-2 text-gray-700" /> : <PlusIcon className="h-5 w-5 mr-2 text-gray-700" />}
                                    {editingImage ? 'Edit Hero Image' : 'Add New Hero Image'}
                                </h3>
                                <button
                                    onClick={resetForm}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-8">
                                <div className="grid md:grid-cols-2 gap-8">
                                    {/* Desktop Image Upload */}
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide flex items-center">
                                            <ComputerDesktopIcon className="h-4 w-4 mr-1.5" />
                                            Desktop Image
                                        </label>
                                        <div className="relative group cursor-pointer">
                                            <div className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all h-64 flex flex-col justify-center items-center ${desktopPreview ? 'border-gray-300 bg-white' : 'border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50'
                                                }`}>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => handleImageUpload(e, 'desktop')}
                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                />
                                                {desktopPreview ? (
                                                    <div className="relative w-full h-full">
                                                        <img
                                                            src={desktopPreview}
                                                            alt="Desktop Preview"
                                                            className="w-full h-full object-cover rounded-xl shadow-sm"
                                                        />
                                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-xl">
                                                            <span className="text-white text-sm font-medium bg-black/50 px-3 py-1.5 rounded-lg backdrop-blur-sm">Change Image</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center text-gray-500">
                                                        <div className="p-3 bg-gray-100 rounded-full mb-3 group-hover:scale-110 transition-transform">
                                                            <ArrowUpTrayIcon className="h-6 w-6 text-gray-600" />
                                                        </div>
                                                        <span className="font-medium text-sm text-gray-900">Click to upload</span>
                                                        <span className="text-xs text-gray-400 mt-1">1920 x 1080 recommended</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Mobile Image Upload */}
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide flex items-center">
                                            <DevicePhoneMobileIcon className="h-4 w-4 mr-1.5" />
                                            Mobile Image
                                        </label>
                                        <div className="relative group cursor-pointer">
                                            <div className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all h-64 flex flex-col justify-center items-center ${mobilePreview ? 'border-gray-300 bg-white' : 'border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50'
                                                }`}>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => handleImageUpload(e, 'mobile')}
                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                />
                                                {mobilePreview ? (
                                                    <div className="relative w-full h-full">
                                                        <img
                                                            src={mobilePreview}
                                                            alt="Mobile Preview"
                                                            className="w-full h-full object-cover rounded-xl shadow-sm"
                                                        />
                                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-xl">
                                                            <span className="text-white text-sm font-medium bg-black/50 px-3 py-1.5 rounded-lg backdrop-blur-sm">Change Image</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center text-gray-500">
                                                        <div className="p-3 bg-gray-100 rounded-full mb-3 group-hover:scale-110 transition-transform">
                                                            <ArrowUpTrayIcon className="h-6 w-6 text-gray-600" />
                                                        </div>
                                                        <span className="font-medium text-sm text-gray-900">Click to upload</span>
                                                        <span className="text-xs text-gray-400 mt-1">1080 x 1920 recommended</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex space-x-3 pt-6 border-t border-gray-200">
                                    <motion.button
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        type="submit"
                                        disabled={loading}
                                        className="bg-gray-900 text-white px-6 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex-1 text-sm"
                                    >
                                        {loading ? 'Saving...' : (editingImage ? 'Update Hero Image' : 'Add Hero Image')}
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        type="button"
                                        onClick={resetForm}
                                        className="bg-white text-gray-700 border border-gray-300 px-6 py-2.5 rounded-xl hover:bg-gray-50 transition-all font-medium text-sm"
                                    >
                                        Cancel
                                    </motion.button>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Hero Images Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence>
                    {heroImages.length === 0 ? (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="col-span-full text-center py-16 bg-gray-50 rounded-3xl border border-dashed border-gray-200"
                        >
                            <PhotoIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-900 font-medium">No hero images found</p>
                            <p className="text-sm text-gray-500 mt-1">Upload images to display on the homepage</p>
                        </motion.div>
                    ) : (
                        heroImages.map((img, index) => (
                            <motion.div
                                key={img.id}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                                className="group relative bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all"
                            >
                                <div className="aspect-video relative bg-gray-100">
                                    {img.desktop_url ? (
                                        <img src={img.desktop_url} alt="Desktop" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                                            <PhotoIcon className="h-8 w-8" />
                                        </div>
                                    )}
                                    <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide">
                                        Desktop
                                    </div>
                                </div>
                                <div className="aspect-[3/4] relative bg-gray-50 border-t border-gray-100">
                                    {img.mobile_url ? (
                                        <img src={img.mobile_url} alt="Mobile" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                                            <DevicePhoneMobileIcon className="h-8 w-8" />
                                        </div>
                                    )}
                                    <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide">
                                        Mobile
                                    </div>
                                </div>

                                {/* Overlay Actions */}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                    <div className="flex space-x-2 transform translate-y-4 group-hover:translate-y-0 transition-transform">
                                        <motion.button
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            onClick={() => handleEdit(img)}
                                            className="bg-white text-gray-900 p-2.5 rounded-full shadow-lg hover:bg-gray-50"
                                            title="Edit"
                                        >
                                            <PencilSquareIcon className="h-5 w-5" />
                                        </motion.button>
                                        <motion.button
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                            onClick={() => handleDelete(img.id)}
                                            className="bg-white text-red-600 p-2.5 rounded-full shadow-lg hover:bg-red-50"
                                            title="Delete"
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </motion.button>
                                    </div>
                                </div>
                            </motion.div>
                        ))
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

export default AdminHeroImages;
